import { Dialog, DialogProps, Divider, Stack } from '@mui/material'
import { useContext, useState } from 'react'
import { EditModeProvider } from 'contexts/edit-mode.context'
import {
  ContentContainer,
  FooterContainer,
} from './edit-address-settings-dialog.styles'
import AddressTabsTitle from './address-tabs-title/address-tabs-title.component'
import MyCompanyForm from './my-company-form/my-company-form.component'
import AddressForm from './address-form/address-form.component'
import ActionButtons from '../action-buttons/action-buttons.component'
import {
  SettingsContext,
  SettingsProviderValue,
} from 'pages/settings/settings.provider'
import {
  useLazyGetUserByIdQuery,
  usePatchUserByIdMutation,
} from 'store/auth/auth.api'
import { getUserId } from 'utils/common'
import TranspotsErrorSnackbar from 'components/transpots-error-snackbar/transpots-error-snackbar.component'

type EditAddressSettingsDialogProps = {
  handleClose: () => void
} & DialogProps

const EditAddressSettingsDialog = ({
  open,
  handleClose,
}: EditAddressSettingsDialogProps) => {
  const [tabIndex, setTabIndex] = useState(0)
  const [errorMessage, setErrorMessage] = useState('')
  const userId = getUserId()

  const { user } = useContext(
    SettingsContext
  ) as unknown as SettingsProviderValue

  const [updateUser, { isError }] = usePatchUserByIdMutation()
  const [getUser] = useLazyGetUserByIdQuery()

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue)
  }

  const handleSave = async () => {
    try {
      const res = await getUser({ userId })
      await updateUser({ ...res.data, ...user }).unwrap()
      if (!isError) {
        await getUser({ userId })
        handleClose()
      } else {
        setErrorMessage('Something went wrong!')
      }
    } catch (err: any) {
      if (err?.status && err?.error) {
        setErrorMessage(`${err?.status}: ${err?.error}`)
      }

      console.error('Failed to update company:', err)
    }
  }

  const renderTab = () => {
    switch (tabIndex) {
      case 0:
        return <MyCompanyForm />
      case 1:
        return <AddressForm />
      default:
        return <MyCompanyForm />
    }
  }

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth>
        <ContentContainer>
          <AddressTabsTitle tabIndex={tabIndex} handleChange={handleChange} />
          <Divider sx={{ border: 0.6, color: 'secondary.main' }} />
          <Stack spacing={2} p={4}>
            <EditModeProvider initialEditMode suppressAutofill>
              {renderTab()}
            </EditModeProvider>
          </Stack>
        </ContentContainer>
        <FooterContainer>
          <ActionButtons
            firstBtnProps={{
              title: 'Cancel',
              onClick: handleClose,
              bgcolor: 'secondary.main',
            }}
            secondBtnProps={{
              title: 'Save',
              bgcolor: 'primary.main',
              onClick: handleSave,
            }}
          />
        </FooterContainer>
      </Dialog>
      <TranspotsErrorSnackbar
        message={errorMessage}
        open={errorMessage !== ''}
        handleClose={() => setErrorMessage('')}
      />
    </>
  )
}

export default EditAddressSettingsDialog
